import { Button, IconButton, Stack, TextField, Modal as MUIModal } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState, FC } from 'react';
import { toast } from 'react-toastify';
import { FAIcon } from '@packages/ui-components';
import { createRootStyle } from '../notes/components/styles';
import PublicAPI from '@/api/TreedisPublicDAO';
import useSSOLogin from '@/hooks/useSSOLogin';
import getTokenData from '@/utils/getTokenData';

const getReCaptchaToken = () => {
  return new Promise((resolve, reject) => {
    if (!process.env.NEXT_PUBLIC_RECAPTCHA_KEY) resolve(null);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_KEY, { action: 'main' })
        .then(resolve)
        .catch(reject);
    });
  });
};

type ModalProps = {
  open: boolean;
  hideBackdrop?: boolean;
  sx?: any;
  onKeyDown?: any;
  children?: any;
};

const Modal: FC<ModalProps> = ({ children, ...props }) => (
  <MUIModal {...props}>{children}</MUIModal>
);

type Props = {
  tour: any;
  onLoginSuccess?: (userId: number) => void;
};

export const CollaboratorsLoginModal = ({ tour, onLoginSuccess }: Props) => {
  const [userId, setUserId] = useState(null);
  const [step, setStep] = useState(1);
  const [isOpen, setIsOpen] = useState(!localStorage.getItem('publicUserToken'));
  const [inputs, setInputs] = useState({ email: '', password: '', pinTwoFA: '' });
  const [showPassword, setShowPassword] = useState(false);

  const ssoResult = useSSOLogin({
    tour,
    onLoginSuccess: (newUserId) => {
      setUserId(newUserId);
      setIsOpen(false);
      onLoginSuccess?.(newUserId);
    },
  });

  const shouldUseSSO = window.location.hostname === 'icl.treedis.com';

  const collaborators = useMemo(() => tour?.users, [tour]);

  const { enabledSSO, handleSSOLogin } = shouldUseSSO
    ? ssoResult
    : {
        enabledSSO: false,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        handleSSOLogin: () => {},
      };

  const handleInputsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;

    setInputs((prevState) => ({
      ...prevState,
      [id]: id === 'pinTwoFA' ? value.replace(/\s/g, '') : value,
    }));
  };

  const handleSubmit = async () => {
    if (!inputs.email || !inputs.password) {
      toast.error('Please fill in credentials');
      return;
    }

    // If we're on step 2 (2FA PIN entry), validate that PIN is entered
    if (step === 2 && (!inputs.pinTwoFA || inputs.pinTwoFA.trim() === '')) {
      toast.error('Please enter 2FA PIN');
      return;
    }

    if (!collaborators?.find((c: any) => c.email === inputs.email?.toLowerCase())) {
      toast.error('You are not a collaborator on this space');
      return;
    }

    const captchaToken = await getReCaptchaToken();

    // Use loginIoT for tours with 2FA enabled, otherwise use regular login
    const res = tour?.iot2FAEnabled
      ? await PublicAPI.loginIoT({
          ...inputs,
          email: inputs.email?.toLowerCase(),
          captchaToken,
        })
      : await PublicAPI.login({
          ...inputs,
          email: inputs.email?.toLowerCase(),
          captchaToken,
        });

    if (res) {
      const { data } = res;
      if (data?.accessToken) {
        localStorage.setItem('publicUserToken', data.accessToken);
        // Dispatch custom event to notify other components about token update
        window.dispatchEvent(new Event('publicUserTokenUpdated'));
        setUserId(Number(data.user.id));
        setIsOpen(false);
        onLoginSuccess?.(Number(data.user.id));
      } else if (tour?.iot2FAEnabled && step === 1) {
        // If 2FA is enabled and we're on step 1, move to step 2 for PIN entry
        setStep(2);
      } else {
        toast.error(res.message || 'Login failed');
      }
    } else if (!res && step === 2) {
      toast.error('Wrong pin');
    } else {
      toast.error('Wrong password or email');
    }
  };

  const handleGuestAccess = () => {
    setIsOpen(false);
    localStorage.setItem('treedisGuest', JSON.stringify({ isGuest: true }));
  };

  const handleEnterPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('publicUserToken');
    const { tokenExpired, userIdFromToken } = getTokenData(token);
    if ((tokenExpired || !token) && !userId) {
      setIsOpen(true);
    }

    if (userIdFromToken && collaborators?.find((c: any) => c.id === Number(userIdFromToken))) {
      setUserId(Number(userIdFromToken));
      setIsOpen(false);
    }
  }, [userId, collaborators]);

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      hideBackdrop
      sx={{
        background: 'rgba(0, 0, 0, 0.4)',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(15px)',
        zIndex: 1400,
      }}
      onKeyDown={handleEnterPress}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        gap={{ xs: 2, md: 3 }}
        sx={createRootStyle()}
      >
        {step === 1 ? (
          <>
            <TextField
              required
              autoFocus
              id="email"
              type="email"
              label="Email"
              value={inputs.email}
              onChange={handleInputsChange}
              sx={{
                '& .MuiInputBase-root , input': {
                  height: { xs: '36px', md: '56px' },
                  lineHeight: { xs: '36px', md: '56px' },
                },
              }}
              inputProps={{
                style: {
                  width: '320px',
                  borderColor: '#FFFFFF',
                  borderRadius: '4px',
                  color: 'white',
                },
              }}
              InputLabelProps={{
                sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
              }}
            />
            <TextField
              required
              id="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={inputs.password}
              onChange={handleInputsChange}
              sx={{
                '& .MuiInputBase-root , input': {
                  height: { xs: '36px', md: '56px' },
                  lineHeight: { xs: '36px', md: '56px' },
                  width: '320px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword((prevState) => !prevState)}
                    sx={{ color: '#FFF' }}
                  >
                    {showPassword ? <FAIcon icon="faEye" /> : <FAIcon icon="faEyeSlash" />}
                  </IconButton>
                ),
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                style: {
                  borderColor: '#FFFFFF',
                  borderRadius: '4px',
                  color: 'white',
                },
              }}
              InputLabelProps={{
                sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
              }}
            />
          </>
        ) : (
          <TextField
            required
            autoFocus
            id="pinTwoFA"
            type="text"
            label="2FA PIN"
            value={inputs.pinTwoFA}
            onChange={handleInputsChange}
            sx={{
              '& .MuiInputBase-root , input': {
                height: { xs: '36px', md: '56px' },
                lineHeight: { xs: '36px', md: '56px' },
              },
            }}
            inputProps={{
              style: {
                width: '320px',
                borderColor: '#FFFFFF',
                borderRadius: '4px',
                color: 'white',
              },
            }}
            InputLabelProps={{
              sx: { top: { xs: '-10px', md: 0 }, '&.MuiInputLabel-shrink': { top: 0 } },
            }}
          />
        )}

        <Stack gap={1} alignItems="center" width="320px">
          <Button
            type="submit"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#2055FF',
              my: { xs: 0, lg: '5px' },
              height: '56px',
              width: { xs: '100%' },
            }}
          >
            Login as Collaborator
          </Button>
          {tour?.guestNotesAccess && (
            <Button
              onClick={handleGuestAccess}
              sx={{
                backgroundColor: '#2055FF',
                my: { xs: 0, lg: '5px' },
                height: '56px',
                width: { xs: '100%' },
              }}
            >
              Enter as Guest
            </Button>
          )}
          {enabledSSO && (
            <Button
              type="submit"
              onClick={handleSSOLogin}
              sx={{
                backgroundColor: '#2055FF',
                my: { xs: 0, lg: '5px' },
                height: '56px',
                width: { xs: '100%' },
              }}
            >
              Login SSO
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
